import React, { useEffect, useState, useRef } from 'react';
import { ArrowRight, Play, Heart, Smartphone, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { siteData } from '../data/mock';

export const Hero = () => {
  const [loaded, setLoaded] = useState(false);
  const parallaxRef = useRef(null);

  useEffect(() => {
    setLoaded(true);

    const handleMouseMove = (e) => {
      if (!parallaxRef.current) return;
      const { clientX, clientY } = e;
      const x = (clientX / window.innerWidth - 0.5) * 20;
      const y = (clientY / window.innerHeight - 0.5) * 20;

      parallaxRef.current.style.transform = `perspective(1000px) rotateX(${-y}deg) rotateY(${x}deg)`;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-32 lg:pt-20 overflow-hidden bg-white dot-pattern">
      {/* Mesh Gradient Background */}
      <div className="absolute inset-0 mesh-gradient opacity-60" />

      {/* Animated Floating Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[5%] w-20 h-20 bg-vibrant-blue/10 rounded-full blur-xl animate-float-slow" />
        <div className="absolute top-[40%] right-[10%] w-32 h-32 bg-vibrant-red/10 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-[20%] left-[20%] w-24 h-24 bg-vibrant-green/10 rounded-full blur-xl animate-float-slow" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10 grid lg:grid-cols-2 gap-12 items-center">

        {/* Left Content */}
        <div className={`transition-all duration-1000 transform ${loaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 border border-slate-200 mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-vibrant-red opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-vibrant-red"></span>
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              The Future of Wedding Invites
            </span>
          </div>

          <h1 className="text-5xl sm:text-7xl font-bold leading-[1.1] tracking-tight mb-6 text-slate-900">
            Tell Your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-vibrant-red via-vibrant-purple to-vibrant-blue">
              Love Story
            </span> <br />
            Digitally.
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed max-w-lg mb-10">
            {siteData.subtitle}
            <br />
            Create a stunning, mobile-first website for your wedding in minutes.
          </p>

          <div className="flex flex-wrap gap-4">
            <Button
              className="h-14 px-8 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-all hover:scale-105 active:scale-95 shadow-xl shadow-slate-900/20"
              onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Start Creating <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              className="h-14 px-8 rounded-full border-2 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all"
              onClick={() => window.open(siteData.templates[0].demoUrl, '_blank')}
            >
              <Play className="mr-2 w-4 h-4 fill-slate-700" /> Demo
            </Button>
          </div>

          <div className="mt-12 flex items-center gap-4 text-sm text-slate-500">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden">
                  <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" />
                </div>
              ))}
            </div>
            <p>Trusted by <span className="font-bold text-slate-900">50+ Couples</span> this month</p>
          </div>
        </div>

        {/* Right Content - 3D Phone Mockups */}
        <div ref={parallaxRef} className="relative hidden lg:block h-[600px] w-full transition-transform duration-100 ease-out">

          {/* Main Card (Center) */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-[500px] bg-slate-900 rounded-[3rem] border-8 border-slate-900 shadow-2xl overflow-hidden glass-card z-20">
            <div className="absolute inset-0 bg-white">
              <img src={siteData.templates[0].image} alt="App Interface" className="w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-8 left-6 right-6 text-white">
                <div className="text-xs font-medium bg-white/20 backdrop-blur-md inline-block px-3 py-1 rounded-full mb-2">RSVP</div>
                <h3 className="text-2xl font-bold">Rajesh & Lalita</h3>
                <p className="text-white/80 text-sm">Dec 12, 2026 • Mumbai</p>
              </div>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="absolute top-20 right-10 p-4 bg-white rounded-2xl shadow-xl shadow-slate-200/50 animate-float-slow z-30 max-w-[180px]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-vibrant-red/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-vibrant-red fill-current" />
              </div>
              <div>
                <div className="text-xs text-slate-500">New RSVP</div>
                <div className="font-bold text-slate-800">Priya accepted</div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-32 left-0 p-4 bg-white rounded-2xl shadow-xl shadow-slate-200/50 animate-float z-30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-vibrant-blue/10 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-vibrant-blue" />
              </div>
              <div>
                <div className="font-bold text-slate-800">Mobile Ready</div>
                <div className="text-xs text-slate-500">Looks great on any device</div>
              </div>
            </div>
          </div>

          <div className="absolute top-40 left-10 p-4 bg-white rounded-2xl shadow-xl shadow-slate-200/50 animate-float z-10 opacity-60 scale-90">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-vibrant-green/10 flex items-center justify-center">
                <Layers className="w-5 h-5 text-vibrant-green" />
              </div>
              <div>
                <div className="font-bold text-slate-800">50+ Templates</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
